import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
/* eslint-disable react/prop-types */
import propTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import { Search } from 'lucide-react';
import { showAlert } from 'state/actions/site';
import { usePortalWalkthrough } from 'tailwind-components/PortalWalkthrough/PortalWalkthroughContext';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import Button from 'tailwind-components/Button/Button';
import FilterAutocomplete from 'tailwind-components/FilterAutocomplete/FilterAutocomplete';
import FlagPill from 'tailwind-components/FlagPill/FlagPill';
import { safePostCall } from 'helpers/firebaseSetup';
import {
  parseZohoJsonOutput,
  parseZohoOkMessageOutput
} from 'helpers/zoho/response-parsers';
import endpoints from 'constants/endpoints';
import OnboardingStatusPill from './OnboardingStatusPill';
import OnboardingProgressStepper from './OnboardingProgressStepper';
import OnboardingCancelConfirmModal from './OnboardingCancelConfirmModal';
import OnboardingBulkCancelModal from './OnboardingBulkCancelModal';
import { fieldLabelClass } from 'tailwind-components/AgentsFiltersToolbar/filterFieldStyles';
import {
  DEFAULT_STATUS_FILTERS,
  ONBOARDING_GROUP_KEYS,
  ONBOARDING_EMPTY_AGENCY,
  TAB_LABELS
} from './onboardingDashboardConstants';
import {
  flattenOnboardingInformation,
  applyOnboardingFilters,
  onboardingFiltersActive,
  buildAgencyFilterOptions,
  getOnboardingRowId
} from './onboardingDashboardUtils';

// status keys are group keys; agency options are plain strings + the empty sentinel
const getStatusOptionLabel = key => TAB_LABELS[key] || key;
const getAgencyOptionLabel = name =>
  name === ONBOARDING_EMPTY_AGENCY ? '—' : name;
const optionValueEquals = (a, b) => a === b;

const TOUR_MOCK_ROWS = [
  {
    npn: '1234567',
    groupKey: 'onboardingSent',
    groupLabel: 'Onboarding Sent to Agent',
    agentName: 'Walkthrough Test Agent',
    relatedToAgency: "O'Neill Insurance Agency",
    agentAgencyId: 'mock-agency-1',
    agentId: 'mock-agent-1',
    startDate: '2025-04-15',
    requiresUplineApproval: false,
    isAgentPartOfAgency: true,
    isOnboardingCancellable: true,
    onboardingPayloadAgencyId: 'mock-agency-1',
    onboardingSent: true,
    onboardingSubmitted: false,
    paSigned: false,
    onboardingComplete: false,
    originalObEmailLogId: 'EML-2025-00142',
    originalObRecordId: 'OBR-2025-00389'
  },
  {
    npn: '7654321',
    groupKey: 'paSigned',
    groupLabel: 'Producer Agreement Signed',
    agentName: 'James Wilson',
    relatedToAgency: "O'Neill Insurance Agency",
    agentAgencyId: 'mock-agency-1',
    agentId: 'mock-agent-2',
    startDate: '2025-03-01',
    requiresUplineApproval: false,
    isAgentPartOfAgency: true,
    isOnboardingCancellable: true,
    onboardingPayloadAgencyId: 'mock-agency-1',
    onboardingSent: true,
    onboardingSubmitted: true,
    paSigned: true,
    onboardingComplete: false
  },
  {
    npn: '9876543',
    groupKey: 'onboardingCompleted',
    groupLabel: 'Onboarding Approved',
    agentName: 'Sophia Ramirez',
    relatedToAgency: "O'Neill Insurance Agency",
    agentAgencyId: 'mock-agency-1',
    agentId: 'mock-agent-3',
    startDate: '2025-01-20',
    requiresUplineApproval: true,
    isAgentPartOfAgency: false,
    isOnboardingCancellable: false,
    onboardingPayloadAgencyId: 'mock-agency-1',
    onboardingSent: true,
    onboardingSubmitted: true,
    paSigned: true,
    onboardingComplete: true
  }
];

// outline/radius match the agents filters; text size/padding match the pre-refactor version
const ONBOARDING_SEARCH_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    fontSize: '0.875rem'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-grey-lighter, #d0d0d1)'
  },
  '& .MuiInputBase-input': {
    py: '8px'
  }
};

async function fetchOnboardingForAgency({ agencyId, agencyName }) {
  try {
    const res = await safePostCall(endpoints.awsProxy.safePost, {
      agencyId: String(agencyId),
      agencyName: agencyName ?? '',
      endpoint: endpoints.zoho.getOnboardingRecords,
      isZohoRequest: true
    });
    if (!res?.data?.details?.output) {
      return {
        ok: false,
        agencyId,
        message: 'Empty response from server'
      };
    }
    const { ok, message, data } = parseZohoJsonOutput(res.data.details.output);
    if (!ok) {
      return {
        ok: false,
        agencyId,
        message: message || 'Request failed'
      };
    }
    return {
      ok: true,
      agencyId,
      responseAgencyId:
        data?.agencyId ?? data?.onboardingInformation?.agencyId ?? null,
      onboardingInformation: data?.onboardingInformation
    };
  } catch (e) {
    console.error(e);
    return {
      ok: false,
      agencyId,
      message: e?.message || 'Request failed'
    };
  }
}

export default function AgencyOnboardingDashboard({ onboardingLoad }) {
  const dispatch = useDispatch();
  const mainZohoId = useSelector(state => state.user.mainZohoId);
  const fetchGeneration = useRef(0);
  const { isActive, currentSectionId } = usePortalWalkthrough() || {};
  const isTourOnThisSection =
    isActive && currentSectionId === 'onboarding-dashboard';

  const [mergedRows, setMergedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allFailed, setAllFailed] = useState(false);
  const [partialFailures, setPartialFailures] = useState([]);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const [searchRaw, setSearchRaw] = useState('');
  const [statusFilters, setStatusFilters] = useState(DEFAULT_STATUS_FILTERS);
  // null = untouched -> follow all agency options (avoids a zero-rows flash on refetch)
  const [agencyFilters, setAgencyFilters] = useState(null);

  const [cancelConfirmRecord, setCancelConfirmRecord] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const [rowSelection, setRowSelection] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPhase, setBulkPhase] = useState('confirm');
  const [bulkRecords, setBulkRecords] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);

  const runFetch = useCallback(
    async agencies => {
      const gen = ++fetchGeneration.current;
      if (!agencies?.length) {
        setMergedRows([]);
        setAllFailed(false);
        setPartialFailures([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setAllFailed(false);
      setPartialFailures([]);
      setBannerDismissed(false);

      const settled = await Promise.all(
        agencies.map(({ agencyId, agencyName }) =>
          fetchOnboardingForAgency({ agencyId, agencyName })
        )
      );

      if (fetchGeneration.current !== gen) return;

      const successes = [];
      const failures = [];
      for (const r of settled) {
        if (r.ok) successes.push(r);
        else failures.push(r);
      }

      if (failures.length === 1) {
        dispatch(
          showAlert({
            message: failures[0].message || 'Failed to load onboarding',
            code: 'error'
          })
        );
      } else if (failures.length > 1) {
        dispatch(
          showAlert({
            message: `Failed to load onboarding for ${failures.length} agencies.`,
            code: 'error'
          })
        );
      }

      const flat = [];
      for (const s of successes) {
        flat.push(
          ...flattenOnboardingInformation(
            s.onboardingInformation,
            s.responseAgencyId ?? s.agencyId ?? null
          )
        );
      }

      const failList = failures.map(f => {
        const meta = agencies.find(
          a => String(a.agencyId) === String(f.agencyId)
        );
        return {
          agencyId: f.agencyId,
          agencyName: meta?.agencyName,
          message: f.message || 'Error'
        };
      });

      setMergedRows(flat);
      setPartialFailures(failList);
      setAllFailed(failures.length > 0 && successes.length === 0);
      setLoading(false);
    },
    [dispatch]
  );

  useEffect(() => {
    const agencies = onboardingLoad.agencies;
    if (!agencies?.length) {
      setMergedRows([]);
      setAllFailed(false);
      setPartialFailures([]);
      setLoading(false);
      return;
    }
    runFetch(agencies);
  }, [onboardingLoad.nonce, onboardingLoad.agencies, runFetch]);

  const handleConfirmCancel = useCallback(
    async record => {
      setIsCancelling(true);
      dispatch(
        showAlert({ message: 'Cancelling onboarding...', code: 'info' })
      );
      try {
        const res = await safePostCall(endpoints.awsProxy.safePost, {
          endpoint: endpoints.zoho.cancelOnboarding,
          isZohoRequest: true,
          adminId: mainZohoId,
          agencyId: record.onboardingPayloadAgencyId ?? null,
          obEmailLogId: record.originalObEmailLogId,
          obRecordId: record.originalObRecordId ?? null
        });
        const { ok, message } = parseZohoOkMessageOutput(
          res?.data?.details?.output
        );
        if (!ok) {
          throw new Error(message || 'Failed to cancel onboarding');
        }
        dispatch(
          showAlert({
            message: message || 'Onboarding successfully cancelled',
            code: 'success'
          })
        );
        setCancelConfirmOpen(false);
        setCancelConfirmRecord(null);
        runFetch(onboardingLoad.agencies);
      } catch (e) {
        console.error(e);
        dispatch(
          showAlert({
            message: e?.message || 'Failed to cancel onboarding',
            code: 'error'
          })
        );
      } finally {
        setIsCancelling(false);
      }
    },
    [dispatch, mainZohoId, runFetch, onboardingLoad.agencies]
  );

  const effectiveRows = useMemo(() => {
    if (isTourOnThisSection && !loading && !allFailed) {
      if (mergedRows.length === 0) return TOUR_MOCK_ROWS;
      return [TOUR_MOCK_ROWS[0], ...mergedRows];
    }
    return mergedRows;
  }, [loading, allFailed, mergedRows, isTourOnThisSection]);

  const agencyFilterOptions = useMemo(
    () => buildAgencyFilterOptions(effectiveRows),
    [effectiveRows]
  );

  // re-seed to all-selected whenever the option list rebuilds (refetch, tour mock rows)
  useEffect(() => {
    setAgencyFilters(null);
  }, [agencyFilterOptions]);

  const effectiveAgencyFilters = agencyFilters ?? agencyFilterOptions;

  const selectedRecords = useMemo(() => {
    const byId = new Map(effectiveRows.map(r => [getOnboardingRowId(r), r]));
    return Object.keys(rowSelection)
      .filter(id => rowSelection[id])
      .map(id => byId.get(id))
      .filter(Boolean)
      .filter(r => r.isOnboardingCancellable === true);
  }, [rowSelection, effectiveRows]);

  const selectedCount = selectedRecords.length;

  const openBulkConfirm = useCallback(() => {
    if (selectedCount === 0) return;
    if (selectedCount === 1) {
      setCancelConfirmRecord(selectedRecords[0]);
      setCancelConfirmOpen(true);
      return;
    }
    setBulkRecords(selectedRecords);
    setBulkResults([]);
    setBulkPhase('confirm');
    setBulkOpen(true);
  }, [selectedCount, selectedRecords]);

  const runBulkCancel = useCallback(async () => {
    setBulkPhase('running');
    try {
      const onboardings = bulkRecords.map(r => ({
        agencyId: r.onboardingPayloadAgencyId ?? null,
        obEmailLogId: r.originalObEmailLogId,
        obRecordId: r.originalObRecordId ?? null
      }));
      const res = await safePostCall(endpoints.awsProxy.safePost, {
        endpoint: endpoints.zoho.cancelOnboardingBulk,
        isZohoRequest: true,
        adminId: mainZohoId,
        onboardings
      });
      const { ok, message, data } = parseZohoJsonOutput(
        res?.data?.details?.output
      );
      if (!ok) throw new Error(message || 'Bulk cancel failed');
      const resultsByLogId = new Map(
        (data?.results ?? []).map(r => [r.obEmailLogId, r])
      );
      setBulkResults(
        bulkRecords.map(r => {
          const match = resultsByLogId.get(r.originalObEmailLogId);
          return {
            id: getOnboardingRowId(r),
            agentName: r.agentName,
            npn: r.npn,
            agencyName: r.relatedToAgency,
            ok: match?.ok ?? false,
            message: match?.message ?? 'No result returned'
          };
        })
      );
    } catch (e) {
      console.error(e);
      const errMsg = e?.message || 'Bulk cancel failed';
      setBulkResults(
        bulkRecords.map(r => ({
          id: getOnboardingRowId(r),
          agentName: r.agentName,
          npn: r.npn,
          agencyName: r.relatedToAgency,
          ok: false,
          message: errMsg
        }))
      );
    }
    setBulkPhase('results');
  }, [bulkRecords, mainZohoId]);

  const closeBulkModal = useCallback(() => {
    if (bulkPhase === 'running') return;
    const wasResults = bulkPhase === 'results';
    setBulkOpen(false);
    setBulkPhase('confirm');
    setBulkRecords([]);
    if (wasResults) {
      setRowSelection({});
      runFetch(onboardingLoad.agencies);
    }
  }, [bulkPhase, runFetch, onboardingLoad.agencies]);

  const tableRows = useMemo(
    () =>
      applyOnboardingFilters(effectiveRows, {
        searchRaw,
        statusFilters,
        agencyFilters: effectiveAgencyFilters
      }),
    [effectiveRows, searchRaw, statusFilters, effectiveAgencyFilters]
  );

  const filtersAreActive = onboardingFiltersActive({
    searchRaw,
    statusFilters,
    agencyFilters: effectiveAgencyFilters,
    agencyOptions: agencyFilterOptions
  });

  const headerCellClass = 'flex h-10 min-h-10 items-center';

  const columns = useMemo(
    () => [
      {
        header: 'Agent',
        accessorKey: 'agentName',
        size: 200,
        Cell: ({ row }) => (
          <span className='block py-0.5 text-sm font-semibold text-black'>
            {row.original.agentName || '—'}
          </span>
        )
      },
      {
        header: 'NPN',
        accessorKey: 'npn',
        size: 120,
        Cell: ({ row }) => (
          <span className='font-mono text-[13px] text-grey'>
            {row.original.npn ?? '—'}
          </span>
        )
      },
      {
        header: 'Agency',
        accessorKey: 'relatedToAgency',
        size: 180,
        Cell: ({ row }) => {
          const name = String(row.original.relatedToAgency ?? '').trim();
          if (!name) {
            return <span className='text-[13px] text-grey'>—</span>;
          }
          return <span className='text-[13px] text-grey'>{name}</span>;
        }
      },
      {
        header: 'Progress',
        accessorKey: 'progress',
        size: 140,
        Cell: ({ row }) => (
          <div className='py-0.5'>
            <OnboardingProgressStepper record={row.original} />
          </div>
        )
      },
      {
        header: 'Status',
        accessorKey: 'groupLabel',
        size: 188,
        Cell: ({ row }) => (
          <div className='py-0.5'>
            <OnboardingStatusPill
              groupKey={row.original.groupKey}
              label={row.original.groupLabel}
            />
          </div>
        )
      },
      {
        header: 'Flags',
        accessorKey: 'flags',
        size: 200,
        Cell: ({ row }) => {
          const flags = (
            Array.isArray(row.original.flags) ? row.original.flags : []
          )
            .map(flag => String(flag ?? '').trim())
            .filter(Boolean);

          if (flags.length === 0) {
            return <span className='text-[13px] text-grey'>—</span>;
          }

          return (
            <div className='flex flex-wrap gap-1 py-0.5'>
              {flags.map((flag, index) => (
                <FlagPill key={`${flag}-${index}`} text={flag} />
              ))}
            </div>
          );
        }
      },
      {
        header: 'Started',
        accessorKey: 'startDate',
        size: 120,
        Cell: ({ row }) => {
          const d = row.original.startDate;
          const formatted =
            d && dayjs(d).isValid() ? dayjs(d).format('MMM D, YYYY') : null;
          return (
            <span className='text-[13px] text-grey'>{formatted || '—'}</span>
          );
        }
      },
      {
        header: 'Actions',
        accessorKey: 'actions',
        size: 120,
        muiTableHeadCellProps: {
          'data-tour': 'onboarding-actions-header'
        },
        Cell: ({ row }) => (
          <div
            className='py-0.5'
            onClick={e => e.stopPropagation()}
            role='presentation'
          >
            <Button
              variant='danger'
              size='xsmall'
              text='Cancel onboarding'
              disabled={
                row.original.isOnboardingCancellable !== true ||
                selectedCount > 0
              }
              data-tour={
                row.original.isOnboardingCancellable === true
                  ? 'onboarding-cancel-btn'
                  : undefined
              }
              onClick={() => {
                if (row.original.isOnboardingCancellable === true) {
                  setCancelConfirmRecord(row.original);
                  setCancelConfirmOpen(true);
                }
              }}
            />
          </div>
        )
      }
    ],
    [selectedCount]
  );

  const table = useMaterialReactTable({
    columns,
    data: tableRows,
    getRowId: row => getOnboardingRowId(row),
    enableSorting: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowSelection: row => row.original.isOnboardingCancellable === true,
    enableMultiRowSelection: true,
    positionToolbarAlertBanner: 'none',
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: 'fit-content',
        width: '100%',
        boxShadow: 'none'
      }
    },
    muiTableBodyRowProps: {
      sx: { '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' } }
    },
    muiTableHeadProps: { 'data-tour': 'onboarding-table' },
    muiTableHeadCellProps: {
      className: headerCellClass
    },
    renderEmptyRowsFallback: () => {
      if (filtersAreActive) {
        return (
          <p className='m-0 py-10 text-center text-sm text-grey'>
            No agents match the current filters
          </p>
        );
      }
      return (
        <p className='m-0 py-10 text-center text-sm text-grey'>
          No onboarding records
        </p>
      );
    }
  });

  let mainContent;
  if (loading) {
    mainContent = (
      <div className='flex flex-col items-center justify-center py-12'>
        <LoadingContent message='Loading onboarding data...' />
      </div>
    );
  } else if (allFailed) {
    mainContent = (
      <div className='flex flex-col items-center gap-3 py-12'>
        <p className='m-0 text-center text-sm text-grey'>
          Could not load onboarding data for the selected agencies.
        </p>
        <Button
          variant='primaryLight'
          size='small'
          text='Retry'
          onClick={() => runFetch(onboardingLoad.agencies)}
        />
      </div>
    );
  } else {
    mainContent = <MaterialReactTable table={table} />;
  }

  return (
    <div className='flex w-full min-w-0 flex-col gap-3 px-4 pb-4 pt-2'>
      {partialFailures.length > 0 &&
      mergedRows.length > 0 &&
      !allFailed &&
      !bannerDismissed ? (
        <Alert
          severity='warning'
          onClose={() => setBannerDismissed(true)}
          sx={{ py: 0 }}
        >
          <span className='text-sm'>Some agencies could not be loaded:</span>
          <ul className='m-0 mt-1 list-disc pl-5 text-sm'>
            {partialFailures.map(f => (
              <li key={f.agencyId}>
                {f.agencyName ? `${f.agencyName} (${f.agencyId})` : f.agencyId}:{' '}
                {f.message}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div
        data-tour='onboarding-filters'
        className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end'
      >
        <div className='flex min-w-[200px] flex-1 flex-col'>
          <span className={fieldLabelClass}>Search onboarding</span>
          <TextField
            size='small'
            fullWidth
            placeholder='Name or NPN...'
            value={searchRaw}
            onChange={e => setSearchRaw(e.target.value)}
            sx={ONBOARDING_SEARCH_SX}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start' sx={{ mr: 0.5 }}>
                  <Search
                    className='size-[18px] shrink-0 text-grey'
                    aria-hidden
                  />
                </InputAdornment>
              )
            }}
            inputProps={{ 'aria-label': 'Search onboarding records' }}
          />
        </div>
        <div className='flex min-w-0 flex-wrap items-end gap-2'>
          <div
            className='flex w-full flex-col sm:w-[280px]'
            data-tour='onboarding-status-filter'
          >
            <span className={fieldLabelClass}>Status</span>
            <FilterAutocomplete
              options={ONBOARDING_GROUP_KEYS}
              value={statusFilters}
              onChange={setStatusFilters}
              placeholder='Select statuses...'
              getOptionLabel={getStatusOptionLabel}
              isOptionEqualToValue={optionValueEquals}
            />
          </div>
          <div className='flex w-full flex-col sm:w-[280px]'>
            <span className={fieldLabelClass}>Agency</span>
            <FilterAutocomplete
              options={agencyFilterOptions}
              value={effectiveAgencyFilters}
              onChange={setAgencyFilters}
              placeholder='Select agencies...'
              getOptionLabel={getAgencyOptionLabel}
              isOptionEqualToValue={optionValueEquals}
            />
          </div>
        </div>
      </div>

      {selectedCount > 0 && !isTourOnThisSection && (
        <div className='sticky top-0 z-10 flex items-center justify-between gap-2 rounded-lg border border-grey-lighter bg-white px-3 py-2 shadow-sm'>
          <span className='text-sm font-medium text-black'>
            {selectedCount} selected
          </span>
          <div className='flex items-center gap-2'>
            <Button
              variant='secondary'
              size='xsmall'
              text='Clear selection'
              onClick={() => setRowSelection({})}
            />
            <Button
              variant='danger'
              size='xsmall'
              text='Cancel onboarding'
              onClick={openBulkConfirm}
            />
          </div>
        </div>
      )}

      <div className='relative min-h-[200px] min-w-0 overflow-auto'>
        {mainContent}
      </div>

      <OnboardingCancelConfirmModal
        record={cancelConfirmRecord}
        open={cancelConfirmOpen}
        isSubmitting={isCancelling}
        onClose={() => {
          if (isCancelling) return;
          setCancelConfirmOpen(false);
          setCancelConfirmRecord(null);
        }}
        onConfirm={handleConfirmCancel}
      />
      <OnboardingBulkCancelModal
        open={bulkOpen}
        phase={bulkPhase}
        records={bulkRecords}
        results={bulkResults}
        onConfirm={runBulkCancel}
        onClose={closeBulkModal}
      />
    </div>
  );
}

AgencyOnboardingDashboard.propTypes = {
  onboardingLoad: propTypes.shape({
    nonce: propTypes.number.isRequired,
    agencies: propTypes.arrayOf(
      propTypes.shape({
        agencyId: propTypes.string.isRequired,
        agencyName: propTypes.string
      })
    ).isRequired
  }).isRequired
};
